import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import TagManager from 'react-gtm-module'
import { useNavigate } from 'react-router-dom'
import LoadComponent from '~components/molecules/load-component/load-component'
import { useGetMe } from '~hooks/queries/me/use-get-me'
import ErrorPage from '~pages/error-page'
import { apiBase } from '../utils/base-url'

interface FetchAuth0ConfigRes {
  audience: string
  domain: string
  clientId: string
  scope: string
}

function fetchAuth0Config(): Promise<FetchAuth0ConfigRes> {
  const requestUrl = `${apiBase()}/auth0`
  return fetch(requestUrl).then((response) => {
    if (response.ok) {
      return response.json()
    }
    console.log('Failed to fetch Auth0 config:', response.statusText)
    throw new Error(response.statusText)
  })
}

export function Auth0ProviderWithConfig({
  children,
}: {
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['/auth0'],
    queryFn: fetchAuth0Config,
  })
  const onRedirectCallback = (appState: any) => {
    navigate(appState?.returnTo || '/')
  }

  if (isLoading) {
    return <LoadComponent />
  }

  return isError || !data ? (
    <ErrorPage />
  ) : (
    <Auth0Provider
      domain={data.domain}
      clientId={data.clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: data.audience,
        scope: data.scope,
      }}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens
    >
      <TrackUser />
      {children}
    </Auth0Provider>
  )
}

function TrackUser() {
  const { isAuthenticated, isLoading: isLoadingAuth0 } = useAuth0()
  const { data } = useGetMe({ isDisabled: !isAuthenticated })

  useEffect(() => {
    if (isLoadingAuth0) return

    if (!isAuthenticated) {
      const tagManagerArgs = {
        dataLayer: {
          job_title: 'not logged in',
          user_type: 'not logged in',
          user_id: 'not logged in',
          pool_partner: 'not logged in',
          user_scope: [],
        },
      }
      TagManager.dataLayer(tagManagerArgs)
      return
    }

    if (data) {
      const tagManagerArgs = {
        dataLayer: {
          job_title: `${!data.isInternal ? 'external' : data.job_title || 'job title not set'}`,
          user_type: `${data.isInternal ? 'internal' : 'external'}`,
          user_id: `${data.sub}`,
          pool_partner: data.isInternal
            ? 'internal'
            : data?.poolPartnerKeys?.[0]
              ? data?.poolPartnerKeys?.[0]
              : 'no pool partner',
          user_scope: sanitizeScope(data.scope || []),
          department: !data.isInternal
            ? 'external'
            : data.msDepartment || 'department not set',
        },
      }
      TagManager.dataLayer(tagManagerArgs)
    }
  }, [isLoadingAuth0, isAuthenticated, data])

  return null
}

function sanitizeScope(scope: string[]): string[] {
  return [
    ...new Set(
      scope.map((s) => {
        return s.replace(/^(\w+):[\w*-]+:(\w+)$/, '$1:{id}:$2')
      }),
    ),
  ]
}
